import { useState, useMemo, useEffect } from "react";
import { TrendingUp, Info, Sparkles, Zap, Wind, Thermometer, ArrowDown, CheckCircle2 } from "lucide-react";
import { APARTMENTS } from "@/data/apartments";
import { useT } from "@/i18n/LanguageProvider";

const fmtEUR = (n) =>
    Math.round(n).toLocaleString("de-DE") + " €";

// ---------------------------------------------------------------
// Rental assumptions for the G21 project
// ---------------------------------------------------------------
//
// The building is designed as an Effizienzhaus 40 with
//   - per-apartment air-to-air heat pumps (heating + cooling)
//   - optional PV system
//   - very hard-wearing, low-maintenance architecture
//
// As a consequence, the landlord has almost no recurring running
// costs to pass through – water/waste water is the only actual
// annual bill (not modelled here as it depends on household size).
//
// Baseline assumption:
//   Cold rent:      18 €/m² net (project average)
//   Flat extras:     1 €/m² (flat-rate operating costs, excl. water)
//   Indexing:      +15 % every 3 years (approx. Mietspiegel trend + 40 % Premium-Aufschlag für Effizienz-Immobilie in A-Lage)
const RENT_PER_M2_DEFAULT = 18;
const OPEX_PER_M2_DEFAULT = 1;
const INDEX_STEP = 3;      // years between steps
const INDEX_RATE = 0.15;   // 15 % per step

const parseSize = (s) =>
    typeof s === "number" ? s : parseFloat(String(s).replace(",", "."));

/**
 * Mietprognose & Rendite
 * - Vorbelegte Werte für alle 4 Wohnungen.
 * - Heute: Kaltmiete, Betriebskostenpauschale, Warmmiete, Brutto-Rendite.
 * - Zukunft: Slider 0–30 Jahre, Indexierung +15 % alle 3 Jahre.
 */
export const RentalYieldForecast = () => {
    const t = useT();
    const [aptId, setAptId] = useState("erdgeschoss");
    const apt = APARTMENTS.find((a) => a.id === aptId) || APARTMENTS[0];
    const size = parseSize(apt.size);

    const [rentPerM2, setRentPerM2] = useState(RENT_PER_M2_DEFAULT);
    const [opexPerM2, setOpexPerM2] = useState(OPEX_PER_M2_DEFAULT);
    const [years, setYears] = useState(10);
    const [indexRate, setIndexRate] = useState(INDEX_RATE * 100); // %

    // Current financing rate, broadcast from MortgageCalculator. Null until known.
    const [financingRate, setFinancingRate] = useState(null);
    const [highlight, setHighlight] = useState(false);

    // Listen for external events:
    //   - g21:financing-rate        → we learn the user's monthly annuity
    //   - g21:select-rental-apartment → an apartment card or the detail dialog
    //                                    selected a specific unit for us
    useEffect(() => {
        const onRate = (e) => {
            const m = e.detail?.monthly;
            if (typeof m === "number" && m > 0) setFinancingRate(m);
        };
        const onSelect = (e) => {
            const id = e.detail?.apartmentId;
            if (id && APARTMENTS.some((a) => a.id === id)) {
                setAptId(id);
                setHighlight(true);
                window.setTimeout(() => setHighlight(false), 2200);
            }
        };
        window.addEventListener("g21:financing-rate", onRate);
        window.addEventListener("g21:select-rental-apartment", onSelect);
        return () => {
            window.removeEventListener("g21:financing-rate", onRate);
            window.removeEventListener("g21:select-rental-apartment", onSelect);
        };
    }, []);

    const handleAptChange = (id) => {
        setAptId(id);
    };

    const today = useMemo(() => {
        const coldMonthly = rentPerM2 * size;
        const opexMonthly = opexPerM2 * size;
        const warmMonthly = coldMonthly + opexMonthly;
        const annualCold = coldMonthly * 12;
        const grossYield = apt.priceValue > 0 ? (annualCold / apt.priceValue) * 100 : 0;
        return { coldMonthly, opexMonthly, warmMonthly, annualCold, grossYield };
    }, [apt, size, rentPerM2, opexPerM2]);

    // Future projection
    const future = useMemo(() => {
        // steps = number of full 3-year blocks up to `years`
        const steps = Math.floor(years / INDEX_STEP);
        const factor = Math.pow(1 + indexRate / 100, steps);
        const futureCold = today.coldMonthly * factor;
        const futureOpex = today.opexMonthly * factor;
        const futureWarm = futureCold + futureOpex;
        // build a small timeline (every INDEX_STEP years, up to `years`)
        const timeline = [];
        for (let y = 0; y <= years; y += INDEX_STEP) {
            const s = Math.floor(y / INDEX_STEP);
            const f = Math.pow(1 + indexRate / 100, s);
            timeline.push({
                year: y,
                cold: today.coldMonthly * f,
                warm: today.warmMonthly * f,
                rentPerM2: rentPerM2 * f,
            });
        }
        return { steps, factor, futureCold, futureOpex, futureWarm, timeline };
    }, [years, indexRate, today, rentPerM2]);

    const increasePct = (future.factor - 1) * 100;

    return (
        <section
            id="mietrendite"
            className={`bg-white py-24 sm:py-32 transition-shadow duration-500 ${highlight ? "shadow-[inset_0_0_0_3px_rgb(217,119,87)]" : ""}`}
            data-testid="rental-yield-section"
        >
            <div className="container-page">
                {/* Header */}
                <div className="grid lg:grid-cols-12 gap-10 mb-12">
                    <div className="lg:col-span-5">
                        <span className="overline">
                            {t({
                                de: "08 / 12 — Mietprognose & Rendite",
                                en: "08 / 12 — Rental forecast & yield",
                            })}
                        </span>
                        <h2 className="h-section mt-4 text-balance">
                            {t({ de: "Vermieten?", en: "Letting it out?" })}
                            <span className="italic text-brand-500">
                                {" "}{t({ de: "So sieht's aus – heute und in Zukunft.", en: "Here's the picture – today and tomorrow." })}
                            </span>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <p className="body-md">
                            {t({
                                de: (
                                    <>
                                        Wir kalkulieren die Wohnungen im G21 mit einer durchschnittlichen Kaltmiete von{" "}
                                        <strong>18 €/m² netto</strong>. Dank hochwertiger Architektur, eigener Luft-Luft-Wärmepumpe pro
                                        Einheit (heizen &amp; kühlen) und inklusiver 2 kW Photovoltaik-Anlage mit Speicher entstehen kaum klassische Nebenkosten –
                                        wir rechnen mit einer <strong>Pauschale von ca. 1 €/m²</strong> (ohne Wasser/Abwasser, ohne
                                        Haushaltsstrom). Mietsteigerungen im hessischen Neubau-A-Lage-Segment nehmen wir mit{" "}
                                        <strong>+15 % alle 3 Jahre</strong> an.
                                    </>
                                ),
                                en: (
                                    <>
                                        We model the G21 apartments with an average cold rent of <strong>€18/sqm net</strong>. Thanks to
                                        premium architecture, individual air-to-air heat pumps (heating &amp; cooling) and an included 2 kW PV
                                        system with battery storage, classical operating costs are minimal – we use a <strong>flat rate of approx. €1/sqm</strong>{" "}
                                        (excl. water, excl. household electricity). We assume rent indexing of <strong>+15 % every 3 years</strong>
                                        {" "}for new builds in prime Hessian locations.
                                    </>
                                ),
                            })}
                        </p>
                    </div>
                </div>

                {/* Info-Chips about the building tech (the reason the flat-rate opex works) */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-stone-200 border border-stone-200 rounded-2xl overflow-hidden mb-10">
                    <InfoChip
                        icon={<Thermometer size={16} className="text-brand-500" />}
                        title={t({ de: "Luft-Luft-Wärmepumpe", en: "Air-to-air heat pump" })}
                        body={t({ de: "Heizen & Kühlen pro Wohnung", en: "Heating & cooling per unit" })}
                    />
                    <InfoChip
                        icon={<Zap size={16} className="text-brand-500" />}
                        title={t({ de: "PV 2 kW inkl. Speicher", en: "2 kW PV incl. storage" })}
                        body={t({ de: "Eigenversorgung inklusive", en: "Self-supply included" })}
                    />
                    <InfoChip
                        icon={<Wind size={16} className="text-brand-500" />}
                        title={t({ de: "Effizienzhaus 40", en: "Energy class 40" })}
                        body={t({ de: "Niedrige Verbrauchskosten", en: "Low utility costs" })}
                    />
                    <InfoChip
                        icon={<Sparkles size={16} className="text-brand-500" />}
                        title={t({ de: "Premium-Architektur", en: "Premium architecture" })}
                        body={t({ de: "Sehr wartungsarm", en: "Very low-maintenance" })}
                    />
                </div>

                {/* Apartment selector */}
                <div className="flex flex-wrap gap-2 mb-8" role="tablist" data-testid="rental-apt-selector">
                    {APARTMENTS.map((a) => (
                        <button
                            key={a.id}
                            type="button"
                            onClick={() => handleAptChange(a.id)}
                            className={`px-4 py-2 text-sm border transition-colors ${
                                aptId === a.id
                                    ? "border-brand-500 bg-brand-500 text-white"
                                    : "border-stone-200 bg-white text-ink hover:border-brand-500"
                            }`}
                            aria-pressed={aptId === a.id}
                            data-testid={`rental-apt-${a.id}`}
                        >
                            <span className="font-heading">{a.shortName || a.name}</span>
                            <span className="hidden sm:inline ml-2 text-xs opacity-80">{a.size} m²</span>
                        </button>
                    ))}
                </div>

                {/* Calculator body */}
                <div className="grid lg:grid-cols-2 gap-px bg-stone-200 border border-stone-200 rounded-2xl overflow-hidden">
                    {/* Inputs */}
                    <div className="bg-white p-8 lg:p-10 space-y-7" data-testid="rental-inputs">
                        <div className="flex items-baseline justify-between">
                            <span className="overline text-brand-500">
                                {t({ de: "Ihre Annahmen", en: "Your assumptions" })}
                            </span>
                            <span className="font-body text-xs text-stone-500">
                                {apt.name} · {apt.size} m² · {fmtEUR(apt.priceValue)}
                            </span>
                        </div>
                        <Slider
                            label={t({ de: "Kaltmiete / m²", en: "Cold rent / sqm" })}
                            value={rentPerM2}
                            min={12}
                            max={24}
                            step={0.5}
                            unit={`${rentPerM2.toFixed(1)} €`}
                            onChange={setRentPerM2}
                            testid="rental-rent-per-m2"
                        />
                        <Slider
                            label={t({ de: "Nebenkosten-Pauschale / m²", en: "Flat-rate extras / sqm" })}
                            value={opexPerM2}
                            min={0}
                            max={2.5}
                            step={0.1}
                            unit={`${opexPerM2.toFixed(1)} €`}
                            onChange={setOpexPerM2}
                            testid="rental-opex-per-m2"
                        />
                        <Slider
                            label={t({ de: "Mietsteigerung pro 3 Jahre", en: "Rent indexing per 3 years" })}
                            value={indexRate}
                            min={0}
                            max={20}
                            step={1}
                            unit={`${indexRate.toFixed(0)} %`}
                            onChange={setIndexRate}
                            testid="rental-index-rate"
                        />
                        <Slider
                            label={t({ de: "Prognose-Zeitraum", en: "Forecast horizon" })}
                            value={years}
                            min={0}
                            max={30}
                            step={1}
                            unit={`${years} ${t({ de: "Jahre", en: "years" })}`}
                            onChange={setYears}
                            testid="rental-years"
                        />

                        <div className="flex items-start gap-3 p-4 rounded-2xl bg-stone-50 border border-stone-200">
                            <Info size={14} className="text-stone-500 shrink-0 mt-0.5" />
                            <p className="font-body text-xs text-stone-600 leading-relaxed">
                                {t({
                                    de: "Wasser/Abwasser nicht enthalten. Haushaltsstrom ebenfalls nicht – wird über die inklusive PV-Anlage mit Speicher teilweise ausgeglichen. Alle Werte sind unverbindliche Indikationen.",
                                    en: "Water / waste water not included. Household electricity not included – partially offset by the included PV system with battery storage. All values are non-binding indications.",
                                })}
                            </p>
                        </div>
                    </div>

                    {/* Results */}
                    <div className="bg-ink text-white p-8 lg:p-10 flex flex-col justify-between" data-testid="rental-results">
                        <div>
                            {/* Today */}
                            <div>
                                <span className="overline text-clay-300">
                                    {t({ de: "Heute · monatlich", en: "Today · monthly" })}
                                </span>
                                <div className="mt-4 space-y-3">
                                    <Row
                                        label={t({ de: "Kaltmiete", en: "Cold rent" })}
                                        value={fmtEUR(today.coldMonthly)}
                                    />
                                    <Row
                                        label={t({ de: "Nebenkosten-Pauschale", en: "Flat-rate extras" })}
                                        value={fmtEUR(today.opexMonthly)}
                                        muted
                                    />
                                    <Row
                                        label={t({ de: "Gesamtmiete (warm, Pauschale)", en: "Total rent (warm, flat-rate)" })}
                                        value={fmtEUR(today.warmMonthly)}
                                        big
                                    />
                                </div>
                                <div className="mt-6 pt-5 border-t border-white/15 grid grid-cols-2 gap-4">
                                    <MicroStat
                                        label={t({ de: "Jahreskaltmiete", en: "Annual cold rent" })}
                                        value={fmtEUR(today.annualCold)}
                                    />
                                    <MicroStat
                                        label={t({ de: "Brutto-Rendite (kalt)", en: "Gross yield (cold)" })}
                                        value={`${today.grossYield.toFixed(2)} %`}
                                    />
                                </div>
                            </div>

                            {/* Future */}
                            <div className="mt-10 pt-6 border-t border-white/15">
                                <div className="flex items-center gap-2 mb-4">
                                    <TrendingUp size={16} className="text-clay-300" />
                                    <span className="overline text-clay-300">
                                        {t({ de: "Prognose · nach", en: "Forecast · in" })} {years}{" "}
                                        {t({ de: "Jahren", en: "years" })}
                                    </span>
                                </div>
                                <div className="font-heading text-5xl text-clay-300" data-testid="rental-future-warm">
                                    {fmtEUR(future.futureWarm)}
                                </div>
                                <div className="font-body text-sm text-white/70 mt-2">
                                    {t({ de: "Warmmiete/Monat · +", en: "Warm rent/month · +" })}
                                    {increasePct.toFixed(1)} %{" "}
                                    {t({ de: "ggü. heute", en: "vs. today" })}
                                </div>

                                {/* Timeline */}
                                {future.timeline.length > 1 && (
                                    <div className="mt-6 overflow-x-auto" data-testid="rental-timeline">
                                        {financingRate !== null && (
                                            <div className="flex items-center gap-2 mb-3 font-body text-[11px] text-white/60 uppercase tracking-[0.12em]">
                                                <CheckCircle2 size={12} className="text-emerald-300" />
                                                {t({ de: "Grün = Kaltmiete deckt Ihre Rate", en: "Green = cold rent covers your rate" })}
                                                <span className="text-white/80 normal-case tracking-normal">
                                                    ({fmtEUR(financingRate)} / Mo.)
                                                </span>
                                            </div>
                                        )}
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="text-white/55 text-[11px] uppercase tracking-[0.18em]">
                                                    <th className="py-2 text-left font-body font-normal">
                                                        {t({ de: "Jahr", en: "Year" })}
                                                    </th>
                                                    <th className="py-2 text-right font-body font-normal">
                                                        €/m²
                                                    </th>
                                                    <th className="py-2 text-right font-body font-normal">
                                                        {t({ de: "Kalt/Mo.", en: "Cold/mo" })}
                                                    </th>
                                                    <th className="py-2 text-right font-body font-normal">
                                                        {t({ de: "Warm/Mo.", en: "Warm/mo" })}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="font-body">
                                                {future.timeline.map((row, i) => {
                                                    const covers = financingRate !== null && row.cold >= financingRate;
                                                    const prev = i > 0 ? future.timeline[i - 1] : null;
                                                    const isBreakEvenRow = covers && (!prev || prev.cold < financingRate);
                                                    return (
                                                        <tr
                                                            key={row.year}
                                                            className={`border-t border-white/10 ${
                                                                covers
                                                                    ? "bg-emerald-400/10 text-emerald-200"
                                                                    : i === future.timeline.length - 1
                                                                    ? "text-clay-300"
                                                                    : "text-white/85"
                                                            }`}
                                                            data-testid={isBreakEvenRow ? "rental-timeline-break-even-row" : undefined}
                                                        >
                                                            <td className="py-1.5 pl-1.5">
                                                                <span className="inline-flex items-center gap-1.5">
                                                                    {isBreakEvenRow && (
                                                                        <CheckCircle2
                                                                            size={14}
                                                                            className="text-emerald-300 shrink-0"
                                                                            aria-label={t({ de: "Break-even", en: "Break-even" })}
                                                                        />
                                                                    )}
                                                                    +{row.year}
                                                                    {isBreakEvenRow && (
                                                                        <span className="ml-1 text-[10px] font-medium uppercase tracking-[0.12em] text-emerald-300">
                                                                            {t({ de: "Break-even", en: "Break-even" })}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </td>
                                                            <td className="py-1.5 text-right">{row.rentPerM2.toFixed(2)}</td>
                                                            <td className="py-1.5 text-right">{fmtEUR(row.cold)}</td>
                                                            <td className="py-1.5 text-right">{fmtEUR(row.warm)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-8 flex items-start gap-3 p-4 bg-clay-300/10 border border-clay-300/30">
                            <Info size={16} className="text-clay-300 shrink-0 mt-0.5" />
                            <p className="font-body text-xs text-white/80 leading-relaxed">
                                <strong className="text-white">{t({ de: "Kontext:", en: "Context:" })}</strong>{" "}
                                {t({
                                    de: (
                                        <>
                                            Bei einer festen <strong>10- oder 15-jährigen Zinsbindung</strong> entwickelt sich die Miete weiter.
                                            Dieser Rechner berücksichtigt die realistische Mietentwicklung bis 30 Jahre – relevant für eine
                                            tragfähige Kapitalanlage. Den passenden Finanzierungsrahmen berechnen Sie im{" "}
                                            <a href="#finanzierung" className="underline decoration-clay-300 underline-offset-2 hover:text-clay-300">
                                                Finanzierungsrechner
                                            </a>.
                                        </>
                                    ),
                                    en: (
                                        <>
                                            With a fixed <strong>10- or 15-year interest period</strong>, rent keeps developing. This forecast
                                            models realistic rent growth up to 30 years – relevant for a sustainable investment. Model the
                                            matching loan in the{" "}
                                            <a href="#finanzierung" className="underline decoration-clay-300 underline-offset-2 hover:text-clay-300">
                                                financing calculator
                                            </a>.
                                        </>
                                    ),
                                })}
                            </p>
                        </div>

                        {/* Sync into financing calculator */}
                        <button
                            type="button"
                            onClick={() => {
                                window.dispatchEvent(
                                    new CustomEvent("g21:sync-financing", {
                                        detail: {
                                            price: apt.priceValue,
                                            parking: apt.parkingPrice ?? 29000,
                                            sizeM2: size,
                                            rentPerM2,
                                            apartmentId: aptId,
                                        },
                                    }),
                                );
                                const el = document.getElementById("finanzierung");
                                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                            }}
                            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-white text-ink font-body font-medium text-sm border border-white hover:bg-clay-50 transition-colors"
                            data-testid="rental-sync-to-financing"
                        >
                            <ArrowDown size={16} />
                            {t({
                                de: "Diese Kaltmiete in die Finanzierung übernehmen",
                                en: "Use this cold rent in the financing calculator",
                            })}
                        </button>
                        <a
                            href="/vermoegensaufbau"
                            className="mt-2 inline-flex items-center justify-center gap-1 w-full px-5 py-2.5 text-xs font-body text-clay-300 underline decoration-clay-300/50 underline-offset-2 hover:text-white"
                            data-testid="rental-link-to-wealth"
                        >
                            {t({
                                de: "Vermögensaufbau über 30 Jahre – eigene Seite mit allen Einheiten →",
                                en: "30-year wealth-building analysis – dedicated page with all units →",
                            })}
                        </a>
                        <p className="mt-2 text-[11px] font-body text-white/55 leading-relaxed">
                            {t({
                                de: "Übernommen werden Kaufpreis, Wohnfläche und Kaltmiete. Betriebskosten sind umlagefähig und werden im Cashflow nicht gegengerechnet.",
                                en: "We transfer the purchase price, living area and cold rent. Operating costs are passed on to tenants and are not deducted from cashflow.",
                            })}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

/* ---------------------------------------------------------------- */

const InfoChip = ({ icon, title, body }) => (
    <div className="bg-white p-5">
        <div className="flex items-center gap-2">{icon}<span className="font-heading text-sm text-ink">{title}</span></div>
        <p className="font-body text-xs text-stone-600 mt-1">{body}</p>
    </div>
);

const Slider = ({ label, value, min, max, step, unit, onChange, testid }) => (
    <div data-testid={testid}>
        <div className="flex items-baseline justify-between mb-2">
            <label className="label-line">{label}</label>
            <span className="font-heading text-xl text-ink">{unit}</span>
        </div>
        <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            className="w-full h-1 appearance-none bg-stone-200 cursor-pointer accent-brand-500"
        />
        <div className="flex justify-between mt-1 text-[10px] font-body text-stone-400 tracking-wide">
            <span>{min.toLocaleString("de-DE")}</span>
            <span>{max.toLocaleString("de-DE")}</span>
        </div>
    </div>
);

const Row = ({ label, value, muted = false, big = false }) => (
    <div className="flex items-baseline justify-between gap-3">
        <span className={`font-body text-xs uppercase tracking-[0.15em] ${muted ? "text-white/55" : "text-white/85"}`}>
            {label}
        </span>
        <span className={`font-heading ${big ? "text-3xl text-white" : muted ? "text-lg text-white/80" : "text-2xl text-white"}`}>
            {value}
        </span>
    </div>
);

const MicroStat = ({ label, value }) => (
    <div>
        <div className="font-body text-[10px] uppercase tracking-[0.18em] text-white/55">{label}</div>
        <div className="font-heading text-xl text-white mt-1">{value}</div>
    </div>
);
